import { HttpErrorResponse } from '@angular/common/http';
import {
  LOAD_FAILURE_COPY,
  LoadFailureClass,
  SILENT_FAILURE_CLASSES,
  classifyHttpStatus,
  formatLoadDiagnosticCode,
  formatToastDiagnosticCode,
} from './sheet-load-trace.util';
import { PAGE_ACCESS_DENIED_COPY } from './page-access-denied.util';

/**
 * The generic "we don't have a more specific sentence for this" copy, shared by
 * every class whose only actionable next step is reporting the code. Mirrors
 * `SUPPORT_CODE_COPY` in `sheet-load-trace.util.ts` — not the SAME constant,
 * because a future wording split between the two mechanisms must not require
 * touching the other.
 */
const SUPPORT_CODE_COPY =
  'Something went wrong while preparing this page. Please contact your admin with the support code.';

/**
 * `RESP`'s copy, unlike every other class here, depends on WHICH page failed —
 * see `isConversionScope`.
 */
const RESP_CONVERSION_COPY =
  "We couldn't find the conversion for this record. Please contact your admin with the support code.";
const RESP_GENERIC_COPY =
  "We couldn't load this record. Please contact your admin with the support code.";

/**
 * Copy for a page-level load failure — deliberately separate from
 * `LOAD_FAILURE_COPY`, whose sentences assume a filing sheet. Only the classes
 * `classifyHttpStatus` can actually produce from an HTTP status are listed —
 * `EMPTY`/`ASSET`/`PARSE`/`BUILD`/`STALL` require a step ledger, a watchdog, or
 * a body-shape check that no page-load site here performs.
 *
 * `AUTH` is imported from `LOAD_FAILURE_COPY` rather than restated — both
 * mechanisms show the identical sentence on the login page the forced-logout
 * redirect lands on (see `AuthFailureNoticeService`), so there is exactly one
 * place to edit that wording.
 *
 * `RESP` here is the GENERIC default (used by every page that isn't about a
 * conversion); `buildPageLoadFailureNotice` substitutes `RESP_CONVERSION_COPY`
 * for the two scopes that are. See `isConversionScope`.
 */
export const PAGE_LOAD_FAILURE_COPY: Partial<Record<LoadFailureClass, string>> = {
  NET: 'Connection lost. Check your internet and try again.',
  AUTH: LOAD_FAILURE_COPY.AUTH,
  PERM: PAGE_ACCESS_DENIED_COPY,
  SRV: SUPPORT_CODE_COPY,
  RESP: RESP_GENERIC_COPY,
};

/**
 * The page-load scopes that genuinely load a conversion — everything else
 * (Profile, User Management, the Dashboard summary tiles) is not one, and
 * `RESP`'s copy must not claim otherwise. `FILING_PREVIEW` is the filing
 * form's own `xbrl-data-listing` read (fileName/status/phase for the
 * conversion being edited) — see `FilingFormContainerComponent`.
 */
const CONVERSION_SCOPES: ReadonlySet<string> = new Set([
  'XBRL_FILES',
  'RECENT_CONVERSION',
  'FILING_PREVIEW',
]);

function resolveDetailCopy(scope: string, failure: LoadFailureClass): string {
  // The filing page's secondary read is a conversion read: a refusal there is
  // the "this filing is not yours" scenario, not "you may not open this page".
  if (failure === 'PERM' && scope === 'FILING_PREVIEW') {
    return LOAD_FAILURE_COPY.PERM;
  }
  if (failure === 'RESP') {
    return CONVERSION_SCOPES.has(scope) ? RESP_CONVERSION_COPY : RESP_GENERIC_COPY;
  }
  return PAGE_LOAD_FAILURE_COPY[failure]!;
}

/** A page-load failure, ready for the calling component to toast. */
export interface PageLoadFailureNotice {
  /**
   * The diagnostic code as the USER sees it, e.g. `MBRS-XBRL_FILES-NET` — or
   * just `MBRS-PERM` for a 403 (see `IDENTIFIER_FREE_FAILURE_CLASSES`). The
   * console line keeps the full `MBRS-<SCOPE>-<CLASS>` code.
   */
  code: string;
  /**
   * The single toast line: the sentence followed by the code, e.g. "Connection
   * lost. Check your internet and try again. MBRS-XBRL_FILES-NET"
   * (P.O., 2026-10-05 — no separate detail line underneath it).
   */
  summary: string;
  /**
   * How this load ended. Exposed so a caller with several load-up API calls
   * can cap their toasts to one per class via `PageLoadFailureToastGate`
   * without re-deriving the class from `code`'s text.
   */
  failure: LoadFailureClass;
  /** True when this class must not be shown — AUTH, already handled elsewhere. */
  silent: boolean;
}

function classifyPageLoadError(error: unknown): LoadFailureClass {
  if (error instanceof HttpErrorResponse) {
    return classifyHttpStatus(error.status);
  }
  // A thrown non-HTTP value has no status to classify — SRV (the catch-all for
  // "reached us, but not a definite rejection") is the closest reachable class
  // rather than crashing here; the console record carries the thrown value.
  return 'SRV';
}

/**
 * Classify a page-load failure and compose its diagnostic notice.
 *
 * Unlike the filing-sheet mechanism this carries no step ledger and no
 * watchdog: every call site here is a single HTTP call, not a multi-step
 * pipeline, so there is nothing to enumerate and no unresolvable await to
 * time out.
 *
 * @param scope One short scope token per call site, e.g. `'XBRL_FILES'`.
 * @param error The error from the failing subscribe — normally an
 *              `HttpErrorResponse`, but any thrown value is accepted.
 */
export function buildPageLoadFailureNotice(scope: string, error: unknown): PageLoadFailureNotice {
  const failure = classifyPageLoadError(error);
  // Same MBRS- prefix as the filing-sheet mechanism's codes — a screenshot from
  // any page in the app reads as one family. The SCOPE token (a name, never
  // `S<n>`) is what keeps a page-load code visually distinct from a sheet
  // code; see sheet-load-trace.util.ts for that half of the vocabulary.
  const code = formatLoadDiagnosticCode(scope, failure, null);
  // What the user sees: identical to `code` except for PERM, which shows a bare
  // `MBRS-PERM` on every page (P.O., 2026-10-12) — one rule shared with the
  // filing-sheet mechanism, so a refused user never learns which page or
  // record it was from the toast.
  const toastCode = formatToastDiagnosticCode(scope, failure, null);

  // Unconditional, including production — the toast deliberately omits the
  // underlying error, so this is the only place a developer can read it. Keeps
  // the FULL code (with its scope), PERM included.
  // `httpStatus` is what tells the SRV catch-all's causes apart (a 500 vs a 502
  // vs an unparseable 200 vs a thrown non-HTTP value, which has none).
  console.error(`[page-load] ${code}`, {
    scope,
    failure,
    httpStatus: error instanceof HttpErrorResponse ? error.status : null,
    error,
  });

  return {
    code: toastCode,
    // Single line (P.O., 2026-10-05): the sentence followed by the code —
    // same layout as the filing-sheet mechanism's toast, see
    // sheet-load-trace.service.ts's `fail()`.
    summary: `${resolveDetailCopy(scope, failure)} ${toastCode}`,
    failure,
    silent: SILENT_FAILURE_CLASSES.has(failure),
  };
}
